import type { AgeDistribution } from "../../../api/work";
import { AGE_COLORS, AGE_OPACITY } from "./pipelineColors";

interface Props {
  x: number;
  /** The strip's bottom edge: the cell's baseline. */
  baselineY: number;
  width: number;
  ages: Record<string, number>;
}

const STRIP_HEIGHT = 3;
const BANDS: Array<keyof AgeDistribution> = ["fresh", "mid", "stale"];

/**
 * How old the tickets in one state are, as three bands along the cell's
 * bottom edge.
 *
 * The route has always computed `age_distribution`; nothing drew it. On
 * wingd (2026-10-08) CORE's To Do read as a count of 139 and nothing on the
 * page said that 125 of them were stale, which is the most actionable thing
 * the diagram knows. The colours were already in `pipelineColors`.
 */
export function AgeStrip({ x, baselineY, width, ages }: Props) {
  const counts = BANDS.map((band) => Math.max(0, ages[band] ?? 0));
  const total = counts.reduce((a, b) => a + b, 0);
  if (total === 0 || width <= 0) return null;

  let offset = x;
  const y = baselineY - STRIP_HEIGHT;
  return (
    <g>
      <title>
        {`fresh ${counts[0]} · mid ${counts[1]} · stale ${counts[2]}`}
      </title>
      {BANDS.map((band, i) => {
        const w = ((counts[i] ?? 0) / total) * width;
        const rect = (
          <rect
            key={band}
            x={offset}
            y={y}
            width={w}
            height={STRIP_HEIGHT}
            fill={AGE_COLORS[band]}
            opacity={AGE_OPACITY[band]}
          />
        );
        offset += w;
        return rect;
      })}
    </g>
  );
}
